import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Mail, RefreshCw, ShieldCheck, Loader2, ArrowLeft, Send, Ban } from "lucide-react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { verifyAndSendClaim } from "@/server/listing-verification.functions";
import {
 loadProvidersFn,
 loadSendLogsFn,
 loadSettingsFn,
 saveSettingsFn,
 type Settings,
} from "./-dashboard.admin.email-verification.functions";

export const Route = createFileRoute("/dashboard/admin/email-verification")({
 component: EmailVerificationPage,
});

interface ProviderRow {
 id: string; practice: string; email: string | null;
 email_verification_status: string | null;
 email_verification_score: number | null;
 email_verification_reason: string | null;
 claim_email_sent: boolean;
 claim_email_sent_at: string | null;
}
interface SendLog { id: string; listing_id: string | null; email: string; send_status: string; resend_message_id: string | null; error_message: string | null; sent_at: string }

function EmailVerificationPage() {
 const { user, loading, primaryRole } = useAuth();
 const navigate = useNavigate();
 const [providers, setProviders] = useState<ProviderRow[]>([]);
 const [logs, setLogs] = useState<SendLog[]>([]);
 const [settings, setSettings] = useState<Settings | null>(null);
 const [busyId, setBusyId] = useState<string | null>(null);
 const verifyFn = useServerFn(verifyAndSendClaim);

 const reload = async () => {
 const [p, l, s] = await Promise.all([
 loadProvidersFn(),
 loadSendLogsFn(),
 loadSettingsFn(),
 ]);
 setProviders((p ?? []) as any);
 setLogs((l ?? []) as any);
 setSettings((s ?? null) as any);
 };

 useEffect(() => {
 if (loading) return;
 if (!user) { navigate({ to: "/login" }); return; }
 if (primaryRole && primaryRole !== "admin") { navigate({ to: "/dashboard" }); return; }
 if (primaryRole === "admin") reload();
 }, [user, loading, primaryRole]);

 if (loading || !user || primaryRole !== "admin") {
 return <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
 }

 const runVerify = async (id: string) => {
 setBusyId(id);
 try {
 const r = await verifyFn({ data: { listing_id: id } });
 if (r.sent) toast.success(`Sent (${r.message_id ?? " "})`);
 else if (r.blocked?.length) toast.warning(`Blocked: ${r.blocked.join(", ")}`);
 else toast.info(`${r.status}${r.reason ? `, ${r.reason}` : ""}`);
 reload();
 } catch (e: any) { toast.error(e.message); } finally { setBusyId(null); }
 };

 const saveSettings = async (patch: Partial<Settings>) => {
 if (!settings) return;
 const next = { ...settings, ...patch };
 setSettings(next);
 try {
 await saveSettingsFn({ data: { patch } });
 } catch (e: any) {
 toast.error(e?.message ?? "Failed to save settings");
 }
 };

 const total = providers.length;
 const counts = providers.reduce<Record<string, number>>((acc, p) => {
 const k = p.email_verification_status ?? "pending";
 acc[k] = (acc[k] ?? 0) + 1; return acc;
 }, {});
 const sentCount = logs.filter((l) => l.send_status === "sent").length;
 const blockedCount = logs.filter((l) => l.send_status === "blocked").length;

 const statusBadge = (s: string | null) => {
 const map: Record<string, string> = {
 valid_safe: "border-primary text-primary",
 valid_but_risky: "border-yellow-500 text-yellow-600",
 role_based: "border-yellow-500 text-yellow-600",
 disposable: "border-destructive text-destructive",
 invalid: "border-destructive text-destructive",
 no_email: "border-border text-muted-foreground",
 };
 return map[s ?? ""] ?? "border-border text-muted-foreground";
 };

 return (
 <div>

 <header className="pt-28 pb-8 border-b border-border bg-card">
 <div className="mx-auto max-w-6xl px-6 flex items-center justify-between gap-4">
 <div>
 <Link to="/dashboard/admin" className="text-xs text-muted-foreground inline-flex items-center gap-1 mb-2"><ArrowLeft className="h-3 w-3" /> Admin</Link>
 <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2"><Mail className="h-6 w-6" /> Email Verification</h1>
 <p className="text-sm text-muted-foreground mt-1">Verify listing emails before sending claim invitations.</p>
 </div>
 <Button variant="outline" size="sm" onClick={reload}><RefreshCw className="h-4 w-4" /> Refresh</Button>
 </div>
 </header>

 <main className="mx-auto max-w-[1400px] px-6 py-6 space-y-10">
 <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
 <Stat label="Total checked" value={total} />
 <Stat label="Valid safe" value={counts.valid_safe ?? 0} />
 <Stat label="Risky" value={(counts.valid_but_risky ?? 0) + (counts.role_based ?? 0)} />
 <Stat label="Invalid" value={(counts.invalid ?? 0) + (counts.disposable ?? 0)} />
 <Stat label="No email" value={counts.no_email ?? 0} />
 <Stat label="Unknown" value={counts.unknown ?? 0} />
 <Stat label="Sent" value={sentCount} icon={<Send className="h-3 w-3" />} />
 <Stat label="Blocked" value={blockedCount} icon={<Ban className="h-3 w-3" />} />
 </section>

 {settings && (
 <section className="border border-border bg-card p-6">
 <h2 className="flex items-center gap-2 text-lg font-semibold mb-4"><ShieldCheck className="h-4 w-4" /> Sending rules</h2>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <SettingNumber label="Minimum score to send" value={settings.min_score} onChange={(v) => saveSettings({ min_score: v })} />
 <SettingNumber label="Daily send limit" value={settings.daily_send_limit} onChange={(v) => saveSettings({ daily_send_limit: v })} />
 <SettingToggle label="Sending enabled" value={settings.resend_enabled} onChange={(v) => saveSettings({ resend_enabled: v })} />
 <SettingToggle label="Allow role-based addresses" value={settings.allow_role_based} onChange={(v) => saveSettings({ allow_role_based: v })} />
 <SettingToggle label="Allow free email providers" value={settings.allow_free_emails} onChange={(v) => saveSettings({ allow_free_emails: v })} />
 <SettingToggle label="Allow catch-all domains" value={settings.allow_catch_all} onChange={(v) => saveSettings({ allow_catch_all: v })} />
 </div>
 </section>
 )}

 <section>
 <h2 className="text-lg font-semibold mb-4">Listings</h2>
 <div className="border border-border bg-card divide-y divide-border">
 {providers.map((p) => (
 <div key={p.id} className="p-4 flex flex-wrap items-center justify-between gap-3">
 <div className="min-w-0">
 <p className="font-medium text-sm truncate">{p.practice}</p>
 <p className="text-xs text-muted-foreground truncate">{p.email ?? "no email"} · score {p.email_verification_score ?? 0}{p.email_verification_reason ? ` · ${p.email_verification_reason}` : ""}</p>
 </div>
 <div className="flex items-center gap-2">
 <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${statusBadge(p.email_verification_status)}`}>{p.email_verification_status ?? "pending"}</span>
 <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${p.claim_email_sent ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>{p.claim_email_sent ? "sent" : "not sent"}</span>
 <Button size="sm" variant="outline" onClick={() => runVerify(p.id)} disabled={busyId === p.id}>
 {busyId === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
 Verify & send
 </Button>
 </div>
 </div>
 ))}
 {providers.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No listings yet.</div>}
 </div>
 </section>

 <section>
 <h2 className="text-lg font-semibold mb-4">Recent send activity</h2>
 <div className="border border-border bg-card divide-y divide-border">
 {logs.map((l) => (
 <div key={l.id} className="p-3 flex flex-wrap items-center justify-between gap-2 text-xs">
 <div className="min-w-0">
 <p className="truncate">{l.email}</p>
 <p className="text-muted-foreground">{new Date(l.sent_at).toLocaleString()}{l.resend_message_id ? ` · ${l.resend_message_id}` : ""}</p>
 </div>
 <span className={`uppercase tracking-wider px-2 py-0.5 border ${l.send_status === "sent" ? "border-primary text-primary" : l.send_status === "blocked" ? "border-yellow-500 text-yellow-600" : "border-destructive text-destructive"}`}>{l.send_status}</span>
 {l.error_message && <span className="text-muted-foreground truncate max-w-xs">{l.error_message}</span>}
 </div>
 ))}
 {logs.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No sends yet.</div>}
 </div>
 </section>
 </main>
 </div>
 );
}

function Stat({ label, value, icon }: { label: string; value: number; icon?: React.ReactNode }) {
 return (
 <div className="border border-border bg-card p-4">
 <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">{icon}{label}</div>
 <p className="text-2xl font-semibold mt-1">{value}</p>
 </div>
 );
}
function SettingNumber({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
 const [v, setV] = useState(String(value));
 useEffect(() => setV(String(value)), [value]);
 return (
 <div className="space-y-1">
 <Label className="text-xs">{label}</Label>
 <Input type="number" value={v} onChange={(e) => setV(e.target.value)} onBlur={() => { const n = Number(v); if (Number.isFinite(n) && n !== value) onChange(n); }} />
 </div>
 );
}
function SettingToggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
 return (
 <div className="flex items-center justify-between border border-border p-3">
 <Label className="text-sm">{label}</Label>
 <Switch checked={value} onCheckedChange={onChange} />
 </div>
 );
}
